import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, SparklesIcon, StarIcon, SlidersHorizontalIcon, LayoutListIcon, LayoutGridIcon, ChevronRightIcon, CalendarIcon } from 'lucide-react';
import AuthorCard from '../../components/Authors/AuthorCard';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import BackButton from '../../components/UI/BackButton';
import { useDarkMode } from '../../context/DarkModeContext';
// Mock Data
interface Author {
  slug: string;
  name: string;
  avatar: string;
  bio: string;
  location: string;
  genres: string[];
  bookCount: number;
  rating: number;
  isDebut: boolean;
  joinedDate: string;
}
const newAuthors: Author[] = [{
  slug: 'olivia-parker',
  name: 'Olivia Parker',
  avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Writing stories that touch the heart. Her debut novel explores the complexity of family dynamics.',
  location: 'New York, NY',
  genres: ['Contemporary', 'Literary Fiction'],
  bookCount: 1,
  rating: 4.8,
  isDebut: true,
  joinedDate: '2026-01-15'
}, {
  slug: 'jackson-reed',
  name: 'Jackson Reed',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Bringing classic noir into the modern age. First time author with a unique voice.',
  location: 'Chicago, IL',
  genres: ['Mystery', 'Noir', 'Crime'],
  bookCount: 1,
  rating: 4.6,
  isDebut: true,
  joinedDate: '2026-02-01'
}, {
  slug: 'sophia-williams',
  name: 'Sophia Williams',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Established romance author now bringing her catalog to LitLaunch. Best known for her "Summer Love" series.',
  location: 'Miami, FL',
  genres: ['Romance', 'Contemporary'],
  bookCount: 8,
  rating: 4.7,
  isDebut: false,
  joinedDate: '2026-01-20'
}, {
  slug: 'liam-oconnor',
  name: "Liam O'Connor",
  avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Sci-fi veteran exploring new frontiers. Author of the bestselling "Starbound" saga.',
  location: 'Dublin, Ireland',
  genres: ['Science Fiction', 'Space Opera'],
  bookCount: 12,
  rating: 4.9,
  isDebut: false,
  joinedDate: '2026-02-05'
}, {
  slug: 'maya-patel',
  name: 'Maya Patel',
  avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Poet and essayist venturing into fiction with her first collection of short stories.',
  location: 'London, UK',
  genres: ['Literary Fiction', 'Poetry'],
  bookCount: 2,
  rating: 4.5,
  isDebut: true,
  joinedDate: '2026-01-10'
}, {
  slug: 'caleb-stone',
  name: 'Caleb Stone',
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Historical fiction writer focusing on untold stories from the 19th century.',
  location: 'Boston, MA',
  genres: ['Historical Fiction'],
  bookCount: 1,
  rating: 4.4,
  isDebut: true,
  joinedDate: '2026-02-10'
}, {
  slug: 'elara-vance',
  name: 'Elara Vance',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Fantasy author known for intricate magic systems. Joining LitLaunch to self-publish her new series.',
  location: 'Seattle, WA',
  genres: ['Fantasy', 'High Fantasy'],
  bookCount: 5,
  rating: 4.8,
  isDebut: false,
  joinedDate: '2026-01-25'
}, {
  slug: 'noah-clarke',
  name: 'Noah Clarke',
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  bio: 'Thriller writer with a background in criminal psychology.',
  location: 'Austin, TX',
  genres: ['Thriller', 'Psychological Thriller'],
  bookCount: 3,
  rating: 4.6,
  isDebut: false,
  joinedDate: '2026-02-08'
}];
// Derive genres
const allGenres = Array.from(new Set(newAuthors.flatMap(a => a.genres))).sort();
const sortOptions = [{
  value: 'newest',
  label: 'Newest Joined'
}, {
  value: 'name-asc',
  label: 'A-Z'
}, {
  value: 'rating-desc',
  label: 'Highest Rated'
}];
const ratingOptions = [{
  value: '4.5',
  label: '4.5+ Stars'
}, {
  value: '4.0',
  label: '4.0+ Stars'
}];
const NewAuthors = () => {
  const {
    darkMode
  } = useDarkMode();
  const [activeTab, setActiveTab] = useState<'all' | 'debut' | 'new-platform'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  // Filter States
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [selectedRatings, setSelectedRatings] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState('newest');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const resetFilters = () => {
    setSelectedGenres([]);
    setSelectedRatings([]);
    setSortBy('newest');
    setSearchQuery('');
    setActiveTab('all');
  };
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  const filterSections: FilterSection[] = [{
    id: 'authorType',
    title: 'Author Type',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All New'
    }, {
      value: 'debut',
      label: 'Debut Authors'
    }, {
      value: 'new-platform',
      label: 'New to LitLaunch'
    }],
    value: activeTab,
    onChange: (val: string) => setActiveTab(val as 'all' | 'debut' | 'new-platform')
  }, {
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: sortOptions,
    value: sortBy,
    onChange: val => setSortBy(val)
  }, {
    id: 'genres',
    title: 'Genres',
    type: 'checkbox',
    options: allGenres.map(g => ({
      value: g,
      label: g
    })),
    value: selectedGenres,
    onChange: val => {
      setSelectedGenres(prev => prev.includes(val) ? prev.filter(g => g !== val) : [...prev, val]);
    }
  }, {
    id: 'ratings',
    title: 'Rating',
    type: 'checkbox',
    options: ratingOptions,
    value: selectedRatings,
    onChange: val => {
      setSelectedRatings(prev => prev.includes(val) ? prev.filter(r => r !== val) : [...prev, val]);
    }
  }];
  const getFilteredAuthors = () => {
    let result = [...newAuthors];
    // Tab Filter
    if (activeTab === 'debut') {
      result = result.filter(a => a.isDebut);
    } else if (activeTab === 'new-platform') {
      result = result.filter(a => !a.isDebut);
    }
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(author => author.name.toLowerCase().includes(query) || author.genres.some(genre => genre.toLowerCase().includes(query)));
    }
    // Genres
    if (selectedGenres.length > 0) {
      result = result.filter(author => author.genres.some(genre => selectedGenres.includes(genre)));
    }
    // Ratings
    if (selectedRatings.length > 0) {
      const minRating = Math.min(...selectedRatings.map(parseFloat));
      result = result.filter(author => author.rating >= minRating);
    }
    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'name-asc':
          return a.name.localeCompare(b.name);
        case 'rating-desc':
          return b.rating - a.rating;
        case 'newest':
        default:
          return new Date(b.joinedDate).getTime() - new Date(a.joinedDate).getTime();
      }
    });
    return result;
  };
  const filteredAuthors = getFilteredAuthors();
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`} data-id="element-7286" data-mp="3pfgx10iku0b9">
      {/* Hero Header */}
      <div className={`relative overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-amber-50'} border-b ${darkMode ? 'border-gray-700' : 'border-amber-100'}`} data-id="element-7287" data-mp="3uea7sl6hbd7q">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-gradient-to-br from-amber-200/20 to-orange-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-7288" data-mp="7bf6hu8u4sxb" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-gradient-to-tr from-pink-200/20 to-rose-200/20 rounded-full blur-3xl pointer-events-none" data-id="element-7289" data-mp="1mtlco6sms6dj" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10" data-id="element-7290" data-mp="1ituehi0rynyz">
          <div className="mb-6" data-id="element-7291" data-mp="1duhnqm6s8wr4">
            <BackButton data-id="element-7292" data-mp="2aio0xxhrsxh0" />
          </div>
          <div className="text-center max-w-3xl mx-auto" data-id="element-7293" data-mp="2ahmdetjkiiie">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold uppercase tracking-wide mb-4" data-id="element-7294" data-mp="26hkm9ebgl7hd">
              <SparklesIcon className="w-3 h-3" data-id="element-7295" data-mp="21orjgvzjw97x" />
              Fresh Voices
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" data-id="element-7296" data-mp="1wr95usal95tl">
              Meet Our Newest Voices
            </h1>
            <p className={`text-lg md:text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-7297" data-mp="2pg6oiwipp4mv">
              Discover the latest storytellers joining LitLaunch. From exciting
              debut authors to established favorites finding a new home.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7298" data-mp="2eavgtpxnnhmn">
        {/* Controls Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8 items-center justify-between" data-id="element-7299" data-mp="2a65q0fhust8l">
          {/* Search */}
          <div className="relative w-full md:w-64" data-id="element-7300" data-mp="2o5f2p56up57t">
            <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-7301" data-mp="2j7dl6op2pdyg" />
            <input type="text" placeholder="Search by name or genre..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-amber-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-amber-500'} focus:ring-2 focus:ring-amber-500/20 focus:outline-none transition-all`} data-id="element-7302" data-mp="2sy86nquhw9iu" />
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto justify-end" data-id="element-7303" data-mp="3cjgt9wf2u4j6">
            {/* Mobile filter button */}
            <button onClick={toggleMobileFilters} className={`md:hidden flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-300' : 'bg-white border-gray-200 text-gray-700'}`} data-id="element-7304" data-mp="3gnoj5wx9tqiz">
              <SlidersHorizontalIcon className="w-4 h-4" data-id="element-7305" data-mp="2sxu9tpc5uk3h" />
              <span className="text-sm font-medium" data-id="element-7306" data-mp="2xucb2f8a80b2">Filters</span>
            </button>

            {/* View toggle — desktop only */}
            <div className={`hidden md:flex items-center p-1 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-7307" data-mp="2jvuqgqdob3xd">
              <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900' : darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-7308" data-mp="uez7gzgu8w7r">
                <LayoutListIcon className="w-5 h-5" data-id="element-7309" data-mp="gkpt462dppsi" />
              </button>
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? darkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900' : darkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-7310" data-mp="1dcjl1ae5k8d2">
                <LayoutGridIcon className="w-5 h-5" data-id="element-7311" data-mp="zfmz9ncw5h31" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-7312" data-mp="3k2zuzjw3omh3">
          {/* Filter Sidebar */}
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={toggleMobileFilters} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} data-id="element-7313" data-mp="1zlqpm3j09x0t">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-7314" data-mp="28ln9lruga7pj" />
          </FilterSidebarWrapper>

          {/* Grid */}
          <div className="flex-1" data-id="element-7315" data-mp="2deg9a1b2apur">
            {filteredAuthors.length > 0 ? <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4' : 'grid-cols-1 md:grid-cols-2 gap-6'}`} data-id="element-7316" data-mp="2iax0x5hgzmcl">
                {filteredAuthors.map(author => <div key={author.slug} className="relative h-full" data-id="element-7317" data-mp="2s81lj0vo39ra">
                    {/* Badge Overlay */}
                    <div className="absolute top-2 right-2 z-10" data-id="element-7318" data-mp="2w9eqz9uulaxo">
                      {author.isDebut ? <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-pink-100 text-pink-800 border border-pink-200" data-id="element-7319" data-mp="11xgk3ek0gr4y">
                          <SparklesIcon className="w-3 h-3 mr-1" data-id="element-7320" data-mp="6lqjdc95y7xl" />
                          Debut
                        </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 border border-blue-200" data-id="element-7321" data-mp="glzjh16a0di9">
                          <StarIcon className="w-3 h-3 mr-1" data-id="element-7322" data-mp="bktqd28peo87" />
                          New
                        </span>}
                    </div>

                    <AuthorCard {...author} variant={viewMode} data-id="element-7323" data-mp="3f4y3t3rt40o7" />
                  </div>)}
              </div> : <div className={`text-center py-20 rounded-2xl border-2 border-dashed ${darkMode ? 'border-gray-800 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`} data-id="element-7324" data-mp="3p1szlewp3mys">
                <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${darkMode ? 'bg-gray-800' : 'bg-white shadow-sm'}`} data-id="element-7325" data-mp="2wcvl7mvluabb">
                  <SearchIcon className={`w-8 h-8 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-7326" data-mp="1gljgspfg9aar" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-7327" data-mp="12vpuoevbx44q">
                  No authors found
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-6`} data-id="element-7328" data-mp="1ulu81tko8qwg">
                  Try adjusting your filters or search terms
                </p>
                <button onClick={resetFilters} className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors" data-id="element-7329" data-mp="1lkcyyv5olph9">
                  Clear Filters
                </button>
              </div>}

            {/* Bottom CTA */}
            {filteredAuthors.length > 0 && <div className="mt-12 text-center" data-id="element-7330" data-mp="2gseujt50chnq">
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} mb-4`} data-id="element-7331" data-mp="2bun1gvsmmphy">
                  Showing {filteredAuthors.length} new voices
                </p>
                <div className={`inline-block h-1 w-20 rounded-full ${darkMode ? 'bg-gray-800' : 'bg-gray-200'}`} data-id="element-7332" data-mp="232l2l1y7hh9s" />
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default NewAuthors;